import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useNotification } from '../../agenticsaas/contexts/NotificationContext';
import { useOrganizationContext } from '../../agenticsaas/contexts/OrganizationContext';
import { 
  LayoutDashboard, 
  AlertTriangle, 
  FileText, 
  DollarSign, 
  Shield, 
  CheckCircle, 
  Inbox, 
  BarChart, 
  CreditCard, 
  Users,
  User,
  UserCheck,
  Bell,
  CheckSquare,
  CalendarPlus,
  FileQuestion,
  Globe,
  ClipboardList,
  Eye,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Circle,
  History,
  Settings,
  Activity,
  MessageCircle,
  HelpCircle,
  Key,
  Sparkles,
  Plus,
  Receipt,
  Target,
  GitBranch,
} from 'lucide-react';

const Sidebar = ({ navigationItems = [] }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { organizationName, loading: organizationLoading } = useOrganizationContext();
  const [isCollapsed, setIsCollapsed] = useState(false);
  
  // State for collapsible sections
  const [expandedSections, setExpandedSections] = useState({
    main: true,
    revenueCycle: true,
    administration: true,
    analytics: true,
    practiceData: true,
    settings: true,
    support: true
  });
  
  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };
  
  // Get unread notification count from SaaSFoundation
  const { unreadCount } = useNotification();

  // Get active route
  const getActiveRoute = () => {
    const currentPath = location.pathname;
    const sorted = [...navigationItems].sort((a, b) => b.path.length - a.path.length);
    const matchingItem = sorted.find(item => {
      if (item.path === '/') {
        return currentPath === '/' || currentPath === '/dashboard';
      }
      return currentPath === item.path || currentPath.startsWith(`${item.path}/`);
    });
    return matchingItem?.id || 'dashboard';
  };

  const activeRoute = getActiveRoute();

  // Icon mapping
  const iconMap = {
    LayoutDashboard,
    AlertTriangle,
    FileText,
    DollarSign,
    Shield,
    CheckCircle,
    Inbox,
    BarChart,
    CreditCard,
    Users,
    User,
    UserCheck,
    Bell,
    CheckSquare,
    CalendarPlus,
    FileQuestion,
    Globe,
    ClipboardList,
    Eye,
    History,
    Settings,
    Activity,
    MessageCircle,
    HelpCircle,
    Key,
    Receipt,
    Target,
    Sparkles,
    GitBranch,
  };

  const getIcon = (iconName) => {
    if (typeof iconName === 'string') {
      return iconMap[iconName] || Circle;
    }
    return iconName || Circle;
  };

  // Organize navigation items into sections
  const mainNavItems = navigationItems.filter(item => 
    ['dashboard', 'work-queue'].includes(item.id)
  );

  // Revenue Cycle: grouped by RCM flow (Patient Billing sits with remittance & A/R — balances/collections after payer processing)
  const revenueCycleAllIds = ['insurance', 'prior-auth', 'charges', 'claims', 'eob', 'ar-posting', 'denials', 'clearinghouse', 'patient-billing'];
  const revenueCycleItems = navigationItems
    .filter(item => revenueCycleAllIds.includes(item.id))
    .sort((a, b) => revenueCycleAllIds.indexOf(a.id) - revenueCycleAllIds.indexOf(b.id));

  const revenueCycleSubgroups = [
    { label: 'Authorization & eligibility', ids: ['insurance', 'prior-auth'] },
    { label: 'Claims & submission', ids: ['charges', 'claims', 'clearinghouse'] },
    { label: 'Remittance & A/R', ids: ['eob', 'ar-posting', 'denials', 'patient-billing'] },
  ];

  const administrationOrder = ['contract-management', 'compliance'];
  const administrationItems = navigationItems
    .filter((item) => administrationOrder.includes(item.id))
    .sort((a, b) => administrationOrder.indexOf(a.id) - administrationOrder.indexOf(b.id));

  // Revenue Cycle Analytics section items (Analytics, Reporting, and Agent Studio)
  const practiceDataAnalyticsItems = navigationItems.filter(item => 
    ['analytics', 'reporting', 'agent-studio'].includes(item.id)
  ).sort((a, b) => {
    // Order: Analytics, Reporting, Agent Studio (last)
    const order = ['analytics', 'reporting', 'agent-studio'];
    return order.indexOf(a.id) - order.indexOf(b.id);
  });

  // Integrations and Data section items
  const practiceDataItems = navigationItems.filter(
    (item) => ['integrations', 'practice-data', 'medical-codes'].includes(item.id)
  );

  // Settings section items (Activity Log, Patient Portal, Settings) — Notifications & Task Manager are in header icons; Team & Subscription are under Settings page
  const settingsItems = navigationItems.filter(item => 
    ['activity', 'patient-portal', 'settings'].includes(item.id)
  ).sort((a, b) => {
    const order = ['activity', 'patient-portal', 'settings'];
    return order.indexOf(a.id) - order.indexOf(b.id);
  });

  // Support section items (Contact Us)
  const supportItems = navigationItems.filter(item => 
    ['contact-us'].includes(item.id)
  );

  // Display name: use "EDI Platform" for clearinghouse (sidebar label)
  const getNavItemLabel = (item) => (item.id === 'clearinghouse' ? 'EDI Platform' : item.name);

  const renderNavItem = (item, { indent = false, showBadge = false } = {}) => {
    const Icon = getIcon(item.icon);
    const isActive = activeRoute === item.id;

    return (
      <button
        key={item.id}
        onClick={() => navigate(item.path)}
        className={`w-full flex items-center py-2.5 text-sm font-medium rounded-lg transition-colors ${
          indent ? 'pl-8 pr-3' : 'px-3'
        } ${
          isActive
            ? 'bg-emerald-500 text-white'
            : 'text-white hover:bg-indigo-700/70'
        }`}
      >
        <Icon className="flex-shrink-0 w-5 h-5 mr-3 text-current" />
        {!isCollapsed && (
          <>
            <span className="flex-1 text-left">{getNavItemLabel(item)}</span>
            {isActive && (
              <Plus className="w-4 h-4 flex-shrink-0 text-white/90" />
            )}
            {showBadge && item.id === 'notifications' && unreadCount > 0 && !isActive && (
              <span className="ml-auto bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                {unreadCount}
              </span>
            )}
            {showBadge && item.badge && item.id !== 'notifications' && !isActive && (
              <span className="ml-auto bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                {item.badge}
              </span>
            )}
          </>
        )}
      </button>
    );
  };

  const sectionButtonClass = 'w-full flex items-center justify-between px-3 py-2 mb-1 text-xs font-semibold text-gray-400 uppercase tracking-wider hover:text-gray-300 rounded-lg transition-colors text-left';
  const sectionLabelClass = 'text-left text-xs font-semibold text-gray-400 uppercase tracking-wider px-3 pt-3 pb-1.5';

  return (
    <div className={`relative flex flex-col h-screen transition-all duration-300 flex-shrink-0 ${
      isCollapsed ? 'w-14' : 'w-56'
    }`} style={{ backgroundColor: '#1e1b4b', borderLeft: '3px solid rgba(30, 27, 75, 0.95)' }}>
      {/* Header: Welcome + Organization name */}
      <div className="flex items-center justify-between px-3 py-3 border-b border-indigo-800/50">
        {!isCollapsed && (
          <div className="flex flex-col flex-1 min-w-0">
            <span className="text-xs font-medium text-indigo-200">Welcome</span>
            <span className="text-sm font-semibold text-white truncate" title={organizationName || undefined}>
              {organizationLoading && !organizationName
                ? 'Loading…'
                : organizationName || ''}
            </span>
          </div>
        )}
        {isCollapsed && (
          <FileText className="w-6 h-6 text-white flex-shrink-0 mx-auto" />
        )}
        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="p-1.5 text-gray-400 hover:text-white hover:bg-indigo-700/50 rounded-lg transition-colors flex-shrink-0 ml-1"
          aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {isCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
        </button>
      </div>

      {/* Getting Started - links to Dashboard (typical entry point) */}
      {!isCollapsed && (
        <button
          onClick={() => navigate('/')}
          className="mx-2 mt-3 flex items-center justify-between px-3 py-2.5 rounded-lg transition-colors text-left"
          style={{ backgroundColor: 'rgba(49, 46, 129, 0.6)' }}
          title="Go to Dashboard"
        >
          <div className="flex items-center min-w-0">
            <Sparkles className="w-5 h-5 text-white flex-shrink-0 mr-2" />
            <span className="text-sm font-medium text-white truncate">Getting Started</span>
          </div>
          <ChevronRight className="w-4 h-4 text-white flex-shrink-0 ml-2" />
        </button>
      )}
      {!isCollapsed && (
        <div className="mx-2 mt-1.5 h-0.5 rounded-full bg-gray-500/30" role="presentation" />
      )}

      {/* Main Navigation */}
      <nav className="flex-1 overflow-y-auto scrollbar-hide py-2 px-2">
        {!isCollapsed && (
          <button onClick={() => toggleSection('main')} className={sectionButtonClass}>
            <span className="text-left">Main</span>
            {expandedSections.main ? <ChevronUp className="w-4 h-4 flex-shrink-0" /> : <ChevronDown className="w-4 h-4 flex-shrink-0" />}
          </button>
        )}
        {expandedSections.main && (
          <div className="space-y-0.5 mb-2">
            {mainNavItems.map(item => renderNavItem(item))}
          </div>
        )}

        <div className="border-t border-indigo-800/40 my-2" />

        {!isCollapsed && (
          <button onClick={() => toggleSection('revenueCycle')} className={sectionButtonClass}>
            <span className="text-left">Revenue Cycle</span>
            {expandedSections.revenueCycle ? <ChevronUp className="w-4 h-4 flex-shrink-0" /> : <ChevronDown className="w-4 h-4 flex-shrink-0" />}
          </button>
        )}
        {expandedSections.revenueCycle && (
          <div className="space-y-3 mb-2">
            {revenueCycleSubgroups.map((sub) => {
              const items = revenueCycleItems.filter(item => sub.ids.includes(item.id));
              if (items.length === 0) return null;
              return (
                <div key={sub.label}>
                  {!isCollapsed && (
                    <div className={sectionLabelClass}>{sub.label}</div>
                  )}
                  <div className="space-y-0.5">
                    {items.map(item => renderNavItem(item))}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="border-t border-indigo-800/40 my-2" />

        {!isCollapsed && (
          <button onClick={() => toggleSection('administration')} className={sectionButtonClass}>
            <span className="text-left">Administration</span>
            {expandedSections.administration ? <ChevronUp className="w-4 h-4 flex-shrink-0" /> : <ChevronDown className="w-4 h-4 flex-shrink-0" />}
          </button>
        )}
        {expandedSections.administration && administrationItems.length > 0 && (
          <div className="space-y-0.5 mb-2">
            {administrationItems.map(item => renderNavItem(item))}
          </div>
        )}

        <div className="border-t border-indigo-800/40 my-2" />

        {!isCollapsed && (
          <button onClick={() => toggleSection('analytics')} className={sectionButtonClass}>
            <span className="text-left">Analytics</span>
            {expandedSections.analytics ? <ChevronUp className="w-4 h-4 flex-shrink-0" /> : <ChevronDown className="w-4 h-4 flex-shrink-0" />}
          </button>
        )}
        {expandedSections.analytics && (
          <div className="space-y-0.5 mb-2">
            {practiceDataAnalyticsItems.map(item => renderNavItem(item))}
          </div>
        )}

        <div className="border-t border-indigo-800/40 my-2" />

        {!isCollapsed && (
          <button onClick={() => toggleSection('practiceData')} className={sectionButtonClass}>
            <span className="text-left">Integrations and Data</span>
            {expandedSections.practiceData ? <ChevronUp className="w-4 h-4 flex-shrink-0" /> : <ChevronDown className="w-4 h-4 flex-shrink-0" />}
          </button>
        )}
        {expandedSections.practiceData && (
          <div className="space-y-0.5 mb-2">
            {practiceDataItems.map((item) => renderNavItem(item, { showBadge: true }))}
          </div>
        )}

        <div className="border-t border-indigo-800/40 my-2" />

        {!isCollapsed && (
          <button onClick={() => toggleSection('settings')} className={sectionButtonClass}>
            <span className="text-left">Settings</span>
            {expandedSections.settings ? <ChevronUp className="w-4 h-4 flex-shrink-0" /> : <ChevronDown className="w-4 h-4 flex-shrink-0" />}
          </button>
        )}
        {expandedSections.settings && (
          <div className="space-y-0.5 mb-2">
            {settingsItems.map(item => renderNavItem(item, false))}
          </div>
        )}

        <div className="border-t border-indigo-800/40 my-2" />

        {!isCollapsed && (
          <button onClick={() => toggleSection('support')} className={sectionButtonClass}>
            <span className="text-left">Support</span>
            {expandedSections.support ? <ChevronUp className="w-4 h-4 flex-shrink-0" /> : <ChevronDown className="w-4 h-4 flex-shrink-0" />}
          </button>
        )}
        {expandedSections.support && (
          <div className="space-y-0.5 mb-2">
            {supportItems.map(item => renderNavItem(item))}
          </div>
        )}
      </nav>
    </div>
  );
};

export default Sidebar;

